import React, { useEffect, useRef, useState } from "react";
import { BrowserQRCodeReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";
import { AlertCircle, Camera, QrCode, X } from "lucide-react";
import { decodePresenceQrValue } from "../../../utils/presenceQr";
import "./FreelancerScanner.scss";

// Hints fixos: restringe o decoder a QR Code e ativa "tenta com mais
// esforço" — mais tolerante a ângulo/distância/contraste, importante
// pra ler um QR exibido em OUTRA TELA (não impresso).
const DECODE_HINTS = new Map([
  [DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE]],
  [DecodeHintType.TRY_HARDER, true],
]);

// Tempo de tolerância antes de sugerir a digitação manual. Frames ruins
// isolados são esperados numa leitura contínua de vídeo — não é erro,
// é normal. Só avisamos o usuário se passar tempo demais sem sucesso.
const NO_MATCH_HINT_MS = 12000;

function waitForVideoElement(ref, { retries = 20, intervalMs = 50 } = {}) {
  return new Promise((resolve, reject) => {
    const attempt = (remaining) => {
      if (ref.current) {
        resolve(ref.current);
        return;
      }
      if (remaining <= 0) {
        reject(new Error("A câmera não inicializou a tempo."));
        return;
      }
      setTimeout(() => attempt(remaining - 1), intervalMs);
    };
    attempt(retries);
  });
}

export default function FreelancerScanner({ onConfirmCode }) {
  const [code, setCode] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [scanHint, setScanHint] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [debugRaw, setDebugRaw] = useState(null); // TEMPORÁRIO
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const processingRef = useRef(false);
  const noMatchTimeoutRef = useRef(null);
  const attemptCountRef = useRef(0);
  const clearNoMatchTimeout = () => {
    if (noMatchTimeoutRef.current) {
      clearTimeout(noMatchTimeoutRef.current);
      noMatchTimeoutRef.current = null;
    }
  };

  const stopScanner = () => {
    controlsRef.current?.stop();
    controlsRef.current = null;

    const stream = videoRef.current?.srcObject;

    if (stream instanceof MediaStream) {
      stream.getTracks().forEach((track) => track.stop());
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    clearNoMatchTimeout();
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const handleScan = async () => {
    if (!window.isSecureContext) {
      setScanError("A câmera só funciona em uma conexão HTTPS.");
      return;
    }

    setScanError(null);
    setScanHint(null);
    setIsScanning(true);
    processingRef.current = false;
    attemptCountRef.current = 0;
    
    noMatchTimeoutRef.current = setTimeout(() => {
      setScanHint(
        "Não conseguimos identificar o QR Code automaticamente. Tente aproximar mais o celular ou digite o código manualmente."
      );
    }, NO_MATCH_HINT_MS);

    try {
      const videoEl = await waitForVideoElement(videoRef);

      const reader = new BrowserQRCodeReader(DECODE_HINTS);

      const onDecode = async (result, _error, callbackControls) => {
        attemptCountRef.current += 1;

        // TEMPORÁRIO — atualiza a cada frame processado, achando algo ou não.
        // Isso prova se o loop de leitura está rodando de verdade.
        // setDebugRaw(`tentativas: ${attemptCountRef.current} | último raw: ${result ? `"${result.getText()}"` : "(nada reconhecido)"}`);

        if (!result || processingRef.current) return;

        const rawText = result.getText();
        const presenceCode = decodePresenceQrValue(rawText);

        if (!presenceCode) {
          return;
        }

        processingRef.current = true;
        clearNoMatchTimeout();

        try {
          callbackControls?.stop();
          stopScanner();

          setIsScanning(false);
          setScanHint(null);
          setCode(presenceCode);

          setDebugRaw(`Enviando pro backend: ${presenceCode}`);

          await onConfirmCode(presenceCode);
        } catch (error) {
          processingRef.current = false;

          // setDebugRaw(`ERRO no backend: ${JSON.stringify(error?.response?.data) || error?.message}`);

          setScanError(
            error?.message || "Não foi possível validar este QR Code."
          );
        }
      };

      const cameras = await BrowserQRCodeReader.listVideoInputDevices();
      const backCamera = cameras.find((camera) =>
        /back|rear|traseira|environment/i.test(camera.label)
      );

      const videoConstraints = {
        facingMode: { ideal: "environment" },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        advanced: [{ focusMode: "continuous" }],
      };

      if (backCamera?.deviceId) {
        videoConstraints.deviceId = { exact: backCamera.deviceId };
      }

      const controls = await reader.decodeFromConstraints(
        { audio: false, video: videoConstraints },
        videoEl,
        onDecode
      );

      controlsRef.current = controls;
    } catch (error) {
      console.error("Erro ao iniciar scanner:", error);

      stopScanner();
      setIsScanning(false);
      setScanError(
        "Não foi possível iniciar a câmera. Verifique a permissão e tente novamente."
      );
    }
  };

  const handleCancelScan = () => {
    stopScanner();
    setIsScanning(false);
    setScanHint(null);
  };

  const handleValidate = async () => {
    if (!code) return;

    try {
      await onConfirmCode(code);
    } catch (error) {
      setScanError(
        error?.message || "Não foi possível validar o código."
      );
    }
  };

  return (
    <div className="fs-container">
      <div className="fs-header">
        <div className="fs-header-icon">
          <QrCode size={20} />
        </div>
        <h4 className="fs-title">Confirme sua chegada</h4>
      </div>

      <div className="fs-body">
        {isMobile && (
          <>
            <button
              className="fs-scan-btn"
              onClick={handleScan}
              disabled={isScanning}
              type="button"
            >
              <Camera size={18} />
              Escanear QR Code
            </button>

            <div className="fs-divider">
              <span>ou</span>
            </div>
          </>
        )}

        {scanError && (
          <p className="fs-error">
            <AlertCircle size={14} />
            {scanError}
          </p>
        )}

        <div className="fs-input-group">
          <input
            type="text"
            className="fs-input"
            placeholder="Digite o código"
            value={code}
            onChange={(event) =>
              setCode(
                event.target.value
                  .toUpperCase()
                  .replace(/[^A-Z0-9]/g, "")
              )
            }
            maxLength={8}
            autoCapitalize="characters"
          />

          <button
            className={`fs-validate-btn ${code.length > 0 ? "fs-validate-btn--active" : ""
              }`}
            onClick={handleValidate}
            disabled={!code}
            type="button"
          >
            Validar
          </button>
        </div>

        <p className="fs-hint">
          Código tem 8 caracteres (ex.: A1B2C3D4)
        </p>
      </div>

      {isScanning && (
        <div className="fs-scan-overlay" onClick={handleCancelScan}>
          <div
            className="fs-scan-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              className="fs-scan-close"
              onClick={handleCancelScan}
              type="button"
              aria-label="Fechar câmera"
            >
              <X size={20} />
            </button>

            <div className="fs-scan-content">
              <div className="fs-scan-frame">
                <video
                  ref={videoRef}
                  className="fs-scan-video"
                  autoPlay
                  muted
                  playsInline
                />

                <div className="fs-scan-corner fs-scan-corner--tl" />
                <div className="fs-scan-corner fs-scan-corner--tr" />
                <div className="fs-scan-corner fs-scan-corner--bl" />
                <div className="fs-scan-corner fs-scan-corner--br" />
                <div className="fs-scan-line" />
              </div>

              <p className="fs-scan-text">
                {scanHint || "Posicione o QR Code no centro da tela"}
              </p>

              {/* TEMPORÁRIO — agora visível DENTRO do modal da câmera */}
              {debugRaw !== null && (
                <p style={{
                  fontSize: "11px",
                  wordBreak: "break-all",
                  background: "#fff3cd",
                  padding: "8px",
                  borderRadius: "6px",
                  color: "#664d03",
                  marginTop: "8px",
                  maxWidth: "280px",
                }}>
                  {debugRaw}
                </p>
              )}
              <button
                className="fs-scan-cancel"
                onClick={handleCancelScan}
                type="button"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}